import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Users, Quote, Calendar, Pin } from "lucide-react";
import SectionHeading, { SectionDivider } from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    eyebrow: "تقييمات العملاء",
    title: "ثقة المستقلين السعوديين تتحدث عن نفسها",
    subtitle: "تقييمات وآراء حقيقية من مستخدمي حاسبة برولانسر — تُعرض فور نشرها وتدعم تحسين دقّة اقتراحات التسعير.",
    noReviews: "لا توجد تقييمات بعد — كن أول من يُقيّم بعد الاستخدام!",
    outOf: "من",
    reviews: "تقييم",
    labels: ["سيّئ", "مقبول", "جيد", "جيد جدًا", "ممتاز"],
    commentsTitle: "آخر تعليقات العملاء",
    commentsSub: "تُحدَّث فور إرسال أي تقييم جديد — بدون انتظار المدوّنة.",
    noComments: "لا توجد تعليقات بعد.",
    showMore: "عرض المزيد",
    showLess: "عرض أقل",
    distribution: "توزيع التقييمات",
  },
  en: {
    eyebrow: "User reviews",
    title: "Trusted by Saudi freelancers",
    subtitle: "Real ratings and reviews from Prolancer Calculator users — refreshed daily to power pricing accuracy.",
    noReviews: "No reviews yet — be the first to rate after using the AI!",
    outOf: "out of",
    reviews: "ratings",
    labels: ["Poor", "Fair", "Good", "Very good", "Excellent"],
    commentsTitle: "Latest customer comments",
    commentsSub: "Updated the moment any new rating is submitted — no weekly blog required.",
    noComments: "No comments yet.",
    showMore: "Show more",
    showLess: "Show less",
    distribution: "Rating distribution",
  },
};

// Build and inject <script type="application/ld+json"> for Review + AggregateRating
// so Google can index these as fresh, rich-result-eligible SEO content.
function injectReviewSchema(items, average, count) {
  if (typeof document === "undefined") return;
  const el = document.getElementById("pl-product-jsonld");
  if (!el || !count) return;
  try {
    const data = JSON.parse(el.textContent);
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": average,
      "reviewCount": count,
      "bestRating": 5,
      "worstRating": 1,
    };
    data.review = (items || []).slice(0, 20).map((it) => ({
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": it.rating, "bestRating": 5, "worstRating": 1 },
      "author": { "@type": "Person", "name": it.display_name || "مستخدم" },
      "datePublished": (it.created_at || "").slice(0, 10),
      "reviewBody": (it.comment || "").slice(0, 500),
      "inLanguage": it.language || "ar",
    }));
    if (!data.review.length) delete data.review;
    el.textContent = JSON.stringify(data);
  } catch {}
}

export function loadReviewSchema() {
  Promise.all([
    axios.get(`${API}/feedback/summary`),
    axios.get(`${API}/feedback/list`, { params: { limit: 20, min_rating: 1, with_comments: true } }),
  ])
    .then(([s, l]) => injectReviewSchema(l.data.items || [], s.data.average || 0, s.data.count || 0))
    .catch(() => {});
}

export default function RatingSummary({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const L = ar ? T.ar : T.en;
  const [summary, setSummary] = useState({ average: 0, count: 0, distribution: {} });
  const [comments, setComments] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const arabicFont = ar ? "font-arabic" : "font-outfit";

  useEffect(() => {
    axios.get(`${API}/feedback/summary`)
      .then((r) => setSummary({
        average: r.data.average || 0,
        count: r.data.count || 0,
        distribution: r.data.distribution || {},
      }))
      .catch(() => {});
    axios.get(`${API}/feedback/list`, { params: { limit: 30, min_rating: 3, with_comments: true } })
      .then((r) => setComments(r.data.items || []))
      .catch(() => {});
  }, []);

  // Inject Review JSON-LD whenever comments load → fresh SEO content daily.
  useEffect(() => {
    if (summary.count > 0) {
      injectReviewSchema(comments, summary.average, summary.count);
    }
  }, [comments, summary]);

  const roundedAvg = Math.round(summary.average);
  const total = Math.max(1, summary.count);
  const dist = summary.distribution || {};
  const visible = showAll ? comments : comments.slice(0, 6);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(ar ? "ar-SA-u-ca-gregory" : "en-US", {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return (iso || "").slice(0, 10); }
  };

  return (
    <section
      id="ratings"
      data-testid="rating-summary-section"
      dir={ar ? "rtl" : "ltr"}
      className={`relative ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}
    >
      <SectionDivider theme={theme} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionHeading
          lang={lang}
          theme={theme}
          icon={<Star className="h-3.5 w-3.5 fill-current" />}
          eyebrow={L.eyebrow}
          title={L.title}
          subtitle={L.subtitle}
        />

        {/* Score + distribution */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div
            data-testid="rating-score-card"
            className={`rounded-3xl border-2 p-7 flex flex-col items-center justify-center text-center ${
              dark ? "bg-[#182138] border-[#18c98f]/40" : "bg-white border-[#18c98f]/40"
            }`}
          >
            {summary.count > 0 ? (
              <>
                <div
                  className={`text-6xl sm:text-7xl font-black font-mono-num ${dark ? "text-[#18c98f]" : "text-[#12a777]"}`}
                  data-testid="rating-average"
                >
                  {summary.average.toFixed(1)}
                </div>
                <div className={`text-sm mt-1 ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${arabicFont}`}>
                  {L.outOf} <span className="font-bold">5</span>
                </div>
                <div className="mt-3 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`h-6 w-6 ${n <= roundedAvg ? "fill-[#18c98f] text-[#18c98f]" : dark ? "text-white/20" : "text-[#2d2c35]/20"}`}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
                <div className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                  dark ? "bg-[#18c98f]/15 text-[#18c98f]" : "bg-[#18c98f]/10 text-[#12a777]"
                } ${arabicFont}`} data-testid="rating-count">
                  <Users className="h-3.5 w-3.5" />
                  <span className="font-mono-num">{summary.count}</span>
                  <span>{L.reviews}</span>
                </div>
              </>
            ) : (
              <div className={`text-center ${arabicFont}`}>
                <Star className={`h-10 w-10 mx-auto mb-2 ${dark ? "text-white/25" : "text-[#2d2c35]/25"}`} strokeWidth={1.5} />
                <div className={`text-sm ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>{L.noReviews}</div>
              </div>
            )}
          </div>

          <div className={`rounded-3xl border p-6 md:col-span-2 ${dark ? "bg-[#182138] border-[#2e3a5c]" : "bg-white border-[#e5e5ea]"}`}>
            <div className={`text-xs uppercase tracking-widest font-bold mb-4 ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${arabicFont}`}>
              {L.distribution}
            </div>
            {[5, 4, 3, 2, 1].map((n) => {
              const cnt = Number(dist[String(n)] || dist[n] || 0);
              const pct = Math.round((cnt / total) * 100);
              return (
                <div key={n} className="flex items-center gap-3 py-1.5">
                  <div className={`flex items-center gap-0.5 w-20 shrink-0 ${arabicFont}`}>
                    <Star className="h-3 w-3 fill-[#18c98f] text-[#18c98f]" strokeWidth={1.5} />
                    <span className={`text-xs font-bold font-mono-num ${dark ? "text-white" : "text-[#182138]"}`}>{n}</span>
                    <span className={`text-[10px] ms-1 ${dark ? "text-white/55" : "text-[#2d2c35]/55"}`}>{L.labels[n - 1]}</span>
                  </div>
                  <div className={`flex-1 h-2 rounded-full overflow-hidden ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#18c98f] to-[#12a777] transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className={`w-14 text-end text-xs font-mono-num ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
                    {pct}% <span className={`${dark ? "text-white/40" : "text-[#2d2c35]/40"}`}>({cnt})</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* User comments — real reviews, powered by /api/feedback/list */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
            <div>
              <div className={`text-lg sm:text-xl font-extrabold ${arabicFont} ${dark ? "text-white" : "text-[#182138]"}`}>
                {L.commentsTitle}
              </div>
              <div className={`text-xs mt-1 ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${arabicFont}`}>
                {L.commentsSub}
              </div>
            </div>
          </div>

          {comments.length === 0 ? (
            <div className={`rounded-2xl border p-6 text-center text-sm ${
              dark ? "bg-[#182138] border-[#2e3a5c] text-white/60" : "bg-white border-[#e5e5ea] text-[#2d2c35]/60"
            } ${arabicFont}`}>
              {L.noComments}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="comments-grid">
                {visible.map((c) => (
                  <article
                    key={c.id}
                    data-testid={`comment-${c.id}`}
                    dir={c.language === "en" ? "ltr" : "rtl"}
                    className={`rounded-2xl border p-5 flex flex-col relative ${
                      c.pinned
                        ? dark
                          ? "bg-[#18c98f]/8 border-[#18c98f]/50 shadow-[0_8px_24px_-10px_rgba(24,201,143,0.35)]"
                          : "bg-[#18c98f]/10 border-[#18c98f]/50 shadow-[0_8px_24px_-10px_rgba(24,201,143,0.3)]"
                        : dark
                        ? "bg-[#182138] border-[#2e3a5c]"
                        : "bg-white border-[#e5e5ea]"
                    }`}
                  >
                    {c.pinned && (
                      <span
                        data-testid={`comment-pinned-badge-${c.id}`}
                        className={`absolute -top-2.5 ${c.language === "en" ? "left-3" : "right-3"} inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white text-[10px] font-extrabold px-2.5 py-1 shadow-[0_4px_10px_-2px_rgba(24,201,143,0.5)] ${arabicFont}`}
                      >
                        <Pin className="h-3 w-3" />
                        {ar ? "تعليق مُثبَّت" : "Pinned review"}
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`h-4 w-4 ${n <= c.rating ? "fill-[#18c98f] text-[#18c98f]" : dark ? "text-white/20" : "text-[#2d2c35]/20"}`}
                            strokeWidth={1.5}
                          />
                        ))}
                      </div>
                      <Quote className={`h-5 w-5 ${dark ? "text-[#18c98f]/40" : "text-[#18c98f]/60"}`} />
                    </div>
                    <blockquote className={`text-sm leading-relaxed flex-1 ${c.language === "en" ? "font-outfit" : "font-arabic"} ${dark ? "text-white/85" : "text-[#2d2c35]"}`}>
                      "{c.comment}"
                    </blockquote>
                    <footer className={`mt-4 pt-3 border-t flex items-center justify-between text-[11px] ${
                      dark ? "border-[#2e3a5c] text-white/55" : "border-[#e5e5ea] text-[#2d2c35]/55"
                    }`}>
                      <span className={`font-bold ${dark ? "text-white/75" : "text-[#2d2c35]/80"}`} dir="ltr">
                        {c.display_name}
                      </span>
                      <span className="inline-flex items-center gap-1" dir="ltr">
                        <Calendar className="h-3 w-3" />
                        {formatDate(c.created_at)}
                      </span>
                    </footer>
                  </article>
                ))}
              </div>
              {comments.length > 6 && (
                <div className="mt-5 flex justify-center">
                  <button
                    data-testid="comments-toggle"
                    onClick={() => setShowAll((v) => !v)}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border font-bold text-sm transition-all ${
                      dark
                        ? "bg-[#182138] border-[#18c98f]/40 text-[#18c98f] hover:bg-[#18c98f]/10"
                        : "bg-white border-[#18c98f]/40 text-[#12a777] hover:bg-[#18c98f]/5"
                    } ${arabicFont}`}
                  >
                    {showAll ? L.showLess : `${L.showMore} (${comments.length - 6})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
